import { useState } from "react";
import { Alert, Button, Collapse, Form, Modal, Radio, Select, Space, Steps, Table, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ApiService from "../../../core/service/api-service";
import DatePicker from "../../../core/components/DatePicker";
import HospitalSelectBox from "../../hospital/HospitalSelectBox";
import ModalitySelectBox from "../../modality/ModalitySelectBox";
import { LocalStorageService } from "../../../core/service/local-storage-service";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import { DATE_OPTIONS, getBeginLocalStr, getBeginStr, getDateFromNow, getEndLocalStr, getEndStr } from "../../../core/utils/date-utils";
import ReportBillingStatusEnum, { billingStatusKey } from "../../../enums/ReportBillingStatusEnum";
import buildBucketItem from "./BucketPanel";

// Sunucudaki ReportBillingDateType / ReconciliationBucket ile birebir.
const DATE_TYPE = { CONFIRM: 0, REPORT: 1, STUDY: 2 };
const BUCKET = {
    MATCHED: 1,
    NOT_MATCHED: 2,
    ALREADY_RECONCILED: 3,
    NOT_FOUND: 4,
    AMBIGUOUS: 5,
};

/**
 * 3 adımlı mutabakat: dosya yükle → Acc No sütununu seç → kovalar.
 *
 * Kovalar sunucuda saklanmaz; her toplu aksiyon aynı girdiyle yeniden türetilir. Bu yüzden
 * ekrandan çıkmadan arka arkaya işaretleme yapılabilir ve her aksiyondan sonra sonuç tazelenir.
 */
export default function ReconciliationModal({ open, onClose, onApplied }) {
    const { t } = useTranslation();
    const { hasPermission } = useUser();

    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);

    const [upload, setUpload] = useState(null);
    const [sheetIndex, setSheetIndex] = useState(0);
    const [accColumn, setAccColumn] = useState(null);

    // Başlık satırının 0 tabanlı index'i; -1 = başlık yok. Sunucu bir tahmin döner, kullanıcı değiştirir.
    const [headerRow, setHeaderRow] = useState(0);

    const [hospitalId, setHospitalId] = useState(null);
    const [modalities, setModalities] = useState([]);
    const [dateType, setDateType] = useState(DATE_TYPE.CONFIRM);
    const [dates, setDates] = useState(() => {
        const range = getDateFromNow(DATE_OPTIONS.LAST_15_DAYS);
        return { begin: range.start, end: range.end };
    });

    const [result, setResult] = useState(null);

    const canFinalize = hasPermission(PERMISSIONS.REPORT_BILLING_FINALIZE);
    const canExcel = hasPermission(PERMISSIONS.REPORT_BILLING_EXCEL);

    const sheet = upload?.sheets?.[sheetIndex];

    const reset = () => {
        setStep(0);
        setUpload(null);
        setSheetIndex(0);
        setAccColumn(null);
        setHeaderRow(0);
        setResult(null);
    }

    const close = () => {
        reset();
        onClose && onClose();
    }

    // Çekim tarihi yerel (Unspecified) saklanıyor; diğer ikisi UTC.
    const buildInput = () => ({
        fileToken: upload?.fileToken,
        fileName: upload?.fileName,
        sheetIndex,
        accColumn,
        headerRow,
        hospitalId,
        modalities,
        dateType,
        dateBegin: dateType === DATE_TYPE.STUDY ? getBeginLocalStr(dates.begin) : getBeginStr(dates.begin),
        dateEnd: dateType === DATE_TYPE.STUDY ? getEndLocalStr(dates.end) : getEndStr(dates.end),
    });

    const uploadFile = async (file) => {
        if (!hospitalId) {
            message.warning(t('BILLING_HOSPITAL_REQUIRED'));
            return Upload.LIST_IGNORE;
        }

        setBusy(true);
        try {
            const response = await ApiService.ReportBilling.UploadReconciliationFile(file);
            if (response?.success) {
                setUpload(response.data);
                setSheetIndex(0);
                setAccColumn(null);
                setHeaderRow(response.data?.sheets?.[0]?.headerRowIndex ?? 0);
                setStep(1);
            }
        }
        catch {
            // uploadRequest hatayı zaten kullanıcıya gösterdi.
        }
        finally {
            setBusy(false);
        }

        // antd'nin kendi yükleme akışı devre dışı: dosyayı biz gönderiyoruz.
        return Upload.LIST_IGNORE;
    }

    const runReconcile = async () => {
        if (accColumn === null || accColumn === undefined) {
            message.warning(t('Acc No Column'));
            return;
        }

        setBusy(true);
        const response = await ApiService.ReportBilling.Reconcile(buildInput());
        setBusy(false);

        if (response.success) {
            setResult(response.data);
            setStep(2);
        }
    }

    const bucketOf = (bucketId) => result?.buckets?.find(x => x.bucket === bucketId);

    const applyBucket = (bucketId, newStatus, title) => {
        const bucket = bucketOf(bucketId);
        const count = bucket?.count || 0;

        if (count === 0)
            return;

        Modal.confirm({
            title,
            content: t('BILLING_BULK_CONFIRM', { count, status: t(billingStatusKey(newStatus)) }),
            okText: t('OK'),
            cancelText: t('Cancel'),
            onOk: async () => {
                setBusy(true);
                const response = await ApiService.ReportBilling.ApplyReconciliation({
                    ...buildInput(),
                    bucket: bucketId,
                    newStatus,
                });

                if (response.success) {
                    const skipped = response.data?.skipped?.length || 0;

                    if (skipped > 0) {
                        message.warning(t('BILLING_BULK_RESULT_SKIPPED', {
                            updated: response.data.updated, skipped,
                        }));
                    }
                    else {
                        message.success(t('BILLING_BULK_RESULT', { updated: response.data?.updated || 0 }));
                    }

                    if (response.data?.logWriteFailed)
                        message.error(t('BILLING_LOG_WRITE_FAILED'));

                    // Kovaları tazele: kullanıcı ekrandan çıkmadan diğer bölümlere devam eder.
                    const refreshed = await ApiService.ReportBilling.Reconcile(buildInput());
                    if (refreshed.success)
                        setResult(refreshed.data);

                    onApplied && onApplied();
                }

                setBusy(false);
            },
        });
    }

    const exportNotFound = async () => {
        const response = await ApiService.ReportBilling.ExportNotFound(buildInput());
        if (response.success)
            ApiService.downloadFile(response.data, response.fileName);
    }

    const sampleRows = sheet?.sampleRows || [];

    // Sütun sayısı sayfanın TAMAMINDAN gelir; başlığın üstündeki birleştirilmiş hücre (hastane adı)
    // yüzünden ilk satır dar olabilir ve o zaman Acc No sütunu listede hiç çıkmaz.
    const columnCount = sheet?.columnCount || sheet?.headers?.length || 0;

    // Sütun adları seçili başlık satırından okunur; "başlık yok" seçilirse sadece sıra numarası.
    const headerValues = headerRow >= 0 ? (sampleRows[headerRow] || []) : [];
    const columnLabel = (index) => headerValues[index]?.trim() || `#${index + 1}`;

    const columnIndexes = Array.from({ length: columnCount }, (_, index) => index);

    const previewColumns = [
        {
            title: '#',
            dataIndex: 'rowNumber',
            key: 'rowNumber',
            width: 48,
            fixed: 'left',
        },
        ...columnIndexes.map(index => ({
            title: columnLabel(index),
            dataIndex: index,
            key: index,
            ellipsis: true,
        })),
    ];

    const previewRows = sampleRows.map((row, rowIndex) => {
        const record = { key: rowIndex, rowIndex, rowNumber: rowIndex + 1 };
        row.forEach((value, columnIndex) => { record[columnIndex] = value; });
        return record;
    });

    return (
        <Modal
            title={t('Reconciliation')}
            open={open}
            onCancel={close}
            width={1100}
            footer={(
                <Space>
                    {step === 1 && (
                        <Button type={'primary'} loading={busy} onClick={runReconcile}>
                            {t('Start Reconciliation')}
                        </Button>
                    )}
                    {step === 2 && (
                        <Button onClick={() => setStep(1)}>{t('Select Column')}</Button>
                    )}
                    <Button onClick={close}>{t('Cancel')}</Button>
                </Space>
            )}
            destroyOnClose
        >
            <Steps
                size={'small'}
                current={step}
                style={{ marginBottom: 16 }}
                items={[
                    { title: t('Upload File') },
                    { title: t('Select Column') },
                    { title: t('Result') },
                ]}
            />

            {step === 0 && (
                <>
                    <Alert type={'info'} showIcon message={t('BILLING_RECONCILE_HINT')} style={{ marginBottom: 16 }} />

                    <Form layout={'vertical'}>
                        <Form.Item label={t('Hospital')} required>
                            <HospitalSelectBox
                                mode={undefined}
                                value={hospitalId}
                                onChange={(value) => setHospitalId(Array.isArray(value) ? value[0] : value)}
                            />
                        </Form.Item>

                        <Form.Item label={t('Modality')} extra={t('BILLING_RECONCILE_MODALITY_HINT')}>
                            <ModalitySelectBox
                                value={modalities}
                                onChange={(value) => setModalities(value || [])}
                            />
                        </Form.Item>

                        <Form.Item label={(
                            <Radio.Group
                                size={'small'}
                                options={[
                                    { label: t('Confirm Date Filter'), value: DATE_TYPE.CONFIRM },
                                    { label: t('Report Date'), value: DATE_TYPE.REPORT },
                                    { label: t('Study Date'), value: DATE_TYPE.STUDY },
                                ]}
                                value={dateType}
                                onChange={(e) => setDateType(e.target.value)}
                            />
                        )}>
                            <Space>
                                <DatePicker
                                    locale={LocalStorageService.getAntLocale()}
                                    value={dates.begin}
                                    showToday={false}
                                    onChange={(date) => setDates({ ...dates, begin: date })}
                                />
                                <DatePicker
                                    locale={LocalStorageService.getAntLocale()}
                                    value={dates.end}
                                    showToday={false}
                                    onChange={(date) => setDates({ ...dates, end: date })}
                                />
                            </Space>
                        </Form.Item>

                        <Form.Item>
                            <Upload beforeUpload={uploadFile} maxCount={1} accept={'.xlsx,.xlsm,.csv'}>
                                <Button icon={<UploadOutlined />} loading={busy} disabled={!hospitalId}>
                                    {t('Upload File')}
                                </Button>
                            </Upload>
                        </Form.Item>
                    </Form>
                </>
            )}

            {step === 1 && sheet && (
                <>
                    <Space style={{ marginBottom: 12 }} wrap>
                        <span>{t('Sheet')}:</span>
                        <Select
                            style={{ minWidth: 200 }}
                            value={sheetIndex}
                            onChange={(value) => {
                                setSheetIndex(value);
                                setAccColumn(null);
                                setHeaderRow(upload?.sheets?.[value]?.headerRowIndex ?? 0);
                            }}
                            options={(upload?.sheets || []).map(s => ({
                                value: s.index,
                                label: `${s.name} (${s.rowCount} ${t('Rows')})`,
                            }))}
                        />

                        <span>{t('Header Row')}:</span>
                        <Select
                            style={{ minWidth: 260 }}
                            value={headerRow}
                            onChange={setHeaderRow}
                            options={[
                                { value: -1, label: t('No Header Row') },
                                ...sampleRows.map((row, index) => ({
                                    value: index,
                                    label: `${index + 1}: ${row.filter(x => x && x.trim()).slice(0, 4).join(' | ') || '-'}`,
                                })),
                            ]}
                        />

                        <span>{t('Acc No Column')}:</span>
                        <Select
                            style={{ minWidth: 220 }}
                            value={accColumn}
                            onChange={setAccColumn}
                            placeholder={t('Select Column')}
                            options={columnIndexes.map(index => ({
                                value: index,
                                label: headerRow >= 0
                                    ? `${headerValues[index]?.trim() || '-'} (#${index + 1})`
                                    : `#${index + 1}`,
                            }))}
                        />
                    </Space>

                    <Table
                        size={'small'}
                        dataSource={previewRows}
                        columns={previewColumns}
                        pagination={false}
                        scroll={{ x: true, y: 240 }}
                        // Başlık ve üstündeki satırlar okunmayacak: soluk gösterilir ki seçim doğrulanabilsin.
                        onRow={(record) => ({
                            style: record.rowIndex <= headerRow ? { opacity: 0.45 } : undefined,
                        })}
                    />
                </>
            )}

            {step === 2 && result && (
                <>
                    <Alert
                        type={'info'}
                        style={{ marginBottom: 12 }}
                        message={(
                            <Space wrap>
                                <span>{t('Rows')}: {result.listRowCount} ({result.listDistinctCount})</span>
                                <span>·</span>
                                <span>{t('Reports')}: {result.systemRowCount}</span>
                            </Space>
                        )}
                    />

                    <Collapse
                        defaultActiveKey={[String(BUCKET.MATCHED)]}
                        items={[
                            buildBucketItem({
                                t,
                                bucket: bucketOf(BUCKET.MATCHED),
                                title: t('BUCKET_MATCHED'),
                                description: t('BUCKET_MATCHED_DESC'),
                                actionLabel: `${t('Mark As')}: ${t(billingStatusKey(ReportBillingStatusEnum.RECONCILED))}`,
                                busy,
                                onAction: () => applyBucket(
                                    BUCKET.MATCHED,
                                    ReportBillingStatusEnum.RECONCILED,
                                    t('BUCKET_MATCHED')
                                ),
                            }),
                            buildBucketItem({
                                t,
                                bucket: bucketOf(BUCKET.NOT_MATCHED),
                                title: t('BUCKET_NOT_MATCHED'),
                                description: t('BUCKET_NOT_MATCHED_DESC'),
                                actionLabel: `${t('Mark As')}: ${t(billingStatusKey(ReportBillingStatusEnum.NOT_MATCHED))}`,
                                busy,
                                onAction: () => applyBucket(
                                    BUCKET.NOT_MATCHED,
                                    ReportBillingStatusEnum.NOT_MATCHED,
                                    t('BUCKET_NOT_MATCHED')
                                ),
                            }),
                            buildBucketItem({
                                t,
                                bucket: bucketOf(BUCKET.ALREADY_RECONCILED),
                                title: t('BUCKET_ALREADY_RECONCILED'),
                                description: t('BUCKET_ALREADY_RECONCILED_DESC'),
                                actionLabel: `${t('Mark As')}: ${t(billingStatusKey(ReportBillingStatusEnum.BILLED))}`,
                                busy,
                                // "Faturalandırıldı" ayrı (üst) izin ister; yetki yoksa buton hiç çıkmaz.
                                onAction: canFinalize ? () => applyBucket(
                                    BUCKET.ALREADY_RECONCILED,
                                    ReportBillingStatusEnum.BILLED,
                                    t('Finalize Billing')
                                ) : null,
                            }),
                            buildBucketItem({
                                t,
                                bucket: bucketOf(BUCKET.NOT_FOUND),
                                title: t('BUCKET_NOT_FOUND'),
                                description: t('BUCKET_NOT_FOUND_DESC'),
                                busy,
                                onExport: canExcel ? exportNotFound : null,
                            }),
                            buildBucketItem({
                                t,
                                bucket: bucketOf(BUCKET.AMBIGUOUS),
                                title: t('BUCKET_AMBIGUOUS'),
                                description: t('BUCKET_AMBIGUOUS_DESC'),
                                busy,
                            }),
                        ]}
                    />
                </>
            )}
        </Modal>
    )
}
